import { z } from "zod";
import type { CreateRoomInput, EditRoomInput } from "../../lib/rooms";
import { ROOM_PASSWORD_MAX_LENGTH } from "../../../shared/room-schema.ts";

export type PasscodeAction = "keep" | "change" | "remove";
// An unknown original uses the empty value as a keep sentinel. Only the
// legacy/fetch-failure trash button requests removal in that case.
export function derivePasscodeAction(
  hasPassword: boolean,
  originalPassword: string | null,
  password: string,
): PasscodeAction {
  if (password === (originalPassword ?? "")) return "keep";
  if (!password) return hasPassword ? "remove" : "keep";
  return "change";
}

export function canAccessCurrentPasscode(room: {
  hasPassword: boolean;
  canCopyCode: boolean;
  codeAvailable: boolean;
}): boolean {
  return room.hasPassword && room.canCopyCode && room.codeAvailable;
}

export type RoomValues = {
  visibility: "public" | "private";
  name: string;
  password: string;
  passcodeAction: PasscodeAction;
  limit: number | null;
};

// Only changed values are length-validated. Public rooms discard passcodes,
// including any unfinished private-room draft.
export const editPasscodeSchema = z
  .object({
    visibility: z.enum(["public", "private"]),
    passcodeAction: z.enum(["keep", "change", "remove"]),
    password: z.string(),
  })
  .superRefine((values, context) => {
    if (values.visibility !== "private" || values.passcodeAction !== "change") return;
    if (values.password.length > ROOM_PASSWORD_MAX_LENGTH) {
      context.addIssue({
        code: "custom",
        path: ["password"],
        message: "invalid-room-password",
      });
    }
  });

export const toCreateInput = (values: RoomValues): CreateRoomInput => ({
  visibility: values.visibility,
  name: values.name,
  password: values.visibility === "private" ? values.password : "",
  maxParticipants: values.limit,
});

// RoomStore.edit treats an omitted password as keep and an empty one as remove.
// Public rooms drop their passcode on the server regardless of the draft action.
export const toEditInput = (values: RoomValues): EditRoomInput => ({
  visibility: values.visibility,
  name: values.name,
  maxParticipants: values.limit,
  ...(values.visibility === "public" || values.passcodeAction === "keep"
    ? {}
    : { password: values.passcodeAction === "remove" ? "" : values.password }),
});

// Loading an existing passcode is not an edit. The passcode action tracks
// changes and undo even when the original secret is unavailable.
export function hasRoomChanges(values: RoomValues, initial: RoomValues, editing: boolean) {
  return (
    values.name !== initial.name ||
    values.visibility !== initial.visibility ||
    values.limit !== initial.limit ||
    (editing ? values.passcodeAction !== "keep" : values.password !== initial.password)
  );
}
